import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Gauge, Minus, Plus, RotateCcw, Zap, Save, CheckCircle2, AlertTriangle, XCircle, ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { WheelVisualizer } from "../components/WheelVisualizer";
import { evaluate, STATUS_META } from "../lib/specs";
import { useMeasurements } from "../context/MeasurementsContext";
import { useVehicle } from "../context/VehicleContext";
import { useUnits } from "../context/UnitsContext";
import { createSession } from "../lib/api";
import { NoVehicle } from "../components/NoVehicle";
import { UnitToggle } from "../components/UnitToggle";
import { AccuracyNote } from "../components/AccuracyNote";

const StepField = ({ label, testId, value, onChange, step = 0.05, hint }) => {
  const bump = (d) => {
    const base = value === "" || value === null ? 0 : Number(value);
    onChange((Math.round((base + d) * 100) / 100).toString());
  };
  return (
    <div>
      <Label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{label}</Label>
      <div className="flex items-center gap-1 mt-1">
        <Button
          type="button" variant="outline" size="icon"
          data-testid={`${testId}-minus`}
          onClick={() => bump(-step)}
          className="h-9 w-9 shrink-0 bg-elevated border-steel text-amber hover:bg-steel hover:text-amber"
        >
          <Minus size={14} />
        </Button>
        <Input
          type="number" step={step} inputMode="decimal"
          data-testid={testId}
          value={value}
          placeholder="—"
          onChange={(e) => onChange(e.target.value)}
          className="h-9 text-center font-mono text-base bg-ink border-steel text-white focus-visible:ring-amber"
        />
        <Button
          type="button" variant="outline" size="icon"
          data-testid={`${testId}-plus`}
          onClick={() => bump(step)}
          className="h-9 w-9 shrink-0 bg-elevated border-steel text-amber hover:bg-steel hover:text-amber"
        >
          <Plus size={14} />
        </Button>
      </div>
      {hint && <p className="font-mono text-[9px] text-slate-600 mt-1 tracking-wide">{hint}</p>}
    </div>
  );
};

const specHint = (s) => `TGT ${s.target >= 0 ? "+" : ""}${s.target.toFixed(2)}° · ${s.min.toFixed(2)}…${s.max.toFixed(2)}`;
export default function Calculator() {
  const { m, setWheel, setFlat, loadFactory, reset, photos } = useMeasurements();
  const { specs } = useVehicle();
  const { toeStep, toeInputValue, toeToDeg, toeNum, fmtToe, fmtToeRange } = useUnits();
  const [saveOpen, setSaveOpen] = useState(false);
  const [meta, setMeta] = useState({ title: "", mileage: "", treadDepth: "", notes: "" });
  const qc = useQueryClient();

  const toeHint = (s) => `TGT ${toeNum(s.target, true)} · ${fmtToeRange(s)}`;

  const results = useMemo(() => (specs ? evaluate(m, specs) : null), [m, specs]);

  const handleLoadFactory = () => {
    loadFactory(specs);
    toast.success("Factory specs loaded", { description: `${specs.vehicle} OEM targets applied.` });
  };

  const handleReset = () => {
    reset();
    toast("Readings cleared");
  };

  const saveMutation = useMutation({
    mutationFn: createSession,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sessions"] });
      setSaveOpen(false);
      setMeta({ title: "", mileage: "", treadDepth: "", notes: "" });
      toast.success("Session saved to log", { description: "View it under Session Log." });
    },
    onError: () => toast.error("Could not save session"),
  });

  const handleSave = () => {
    const toNum = (v) => (v === "" ? null : Number(v));
    saveMutation.mutate({
      title: meta.title || `Alignment · ${new Date().toLocaleDateString()}`,
      mileage: meta.mileage ? Number(meta.mileage) : null,
      treadDepth: meta.treadDepth || null,
      notes: meta.notes || null,
      measurements: {
        frontLeft: { camber: toNum(m.frontLeft.camber), caster: toNum(m.frontLeft.caster) },
        frontRight: { camber: toNum(m.frontRight.camber), caster: toNum(m.frontRight.caster) },
        rearLeft: { camber: toNum(m.rearLeft.camber) },
        rearRight: { camber: toNum(m.rearRight.camber) },
        frontToe: toNum(m.frontToe),
        rearToe: toNum(m.rearToe),
      },
      results: { checks: results.checks, cross: results.cross },
      passRate: results.passRate,
      photos: Object.entries(photos).map(([corner, p]) => ({ corner, ...p })),
    });
  };

  if (!specs) return <NoVehicle tkey="novehicle.pickCalc" />;

  const scoreColor = results.filled === 0 ? "#64748B" : results.hasFail ? "#EF4444" : results.passing < results.filled ? "#F59E0B" : "#10B981";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 animate-rise">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
            <Gauge size={14} /> Thortek Performance
          </div>
          <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">
            Alignment Calculator
          </h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl">
            Enter your measured camber, caster and toe. Each reading is scored live against {specs.vehicle} factory tolerances.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-load-factory-specs" onClick={handleLoadFactory}
            className="bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold">
            <Zap size={15} /> Load Factory Specs
          </Button>
          <Button data-testid="btn-reset" onClick={handleReset} variant="outline"
            className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">
            <RotateCcw size={15} /> Reset
          </Button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 animate-rise">
        <UnitToggle />
        <AccuracyNote compact className="flex-1 min-w-[260px]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* INPUTS */}
        <div className="lg:col-span-8 space-y-6">
          {/* FRONT AXLE */}
          <section data-testid="section-front-axle" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Front Axle</h2>
              <span className="font-mono text-[10px] text-amber tracking-widest uppercase px-2 py-1 rounded-sm bg-amber/10">Adjustable</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <StepField label="FL Camber" testId="input-fl-camber" value={m.frontLeft.camber} onChange={(v) => setWheel("frontLeft", "camber", v)} hint={specHint(specs.front.camber)} />
              <StepField label="FR Camber" testId="input-fr-camber" value={m.frontRight.camber} onChange={(v) => setWheel("frontRight", "camber", v)} hint={specHint(specs.front.camber)} />
              <StepField label="FL Caster" testId="input-fl-caster" value={m.frontLeft.caster} onChange={(v) => setWheel("frontLeft", "caster", v)} hint={specHint(specs.front.caster)} />
              <StepField label="FR Caster" testId="input-fr-caster" value={m.frontRight.caster} onChange={(v) => setWheel("frontRight", "caster", v)} hint={specHint(specs.front.caster)} />
              <div className="col-span-2">
                <StepField label="Front Total Toe" testId="input-front-toe" step={toeStep} value={toeInputValue(m.frontToe)} onChange={(v) => setFlat("frontToe", v === "" ? "" : toeToDeg(v))} hint={toeHint(specs.front.totalToe)} />
              </div>
            </div>
          </section>

          {/* REAR AXLE */}
          <section data-testid="section-rear-axle" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold uppercase tracking-widest text-lg">Rear Axle</h2>
              <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase px-2 py-1 rounded-sm bg-steel/30">Solid Axle · Fixed</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-5">
              <StepField label="RL Camber" testId="input-rl-camber" value={m.rearLeft.camber} onChange={(v) => setWheel("rearLeft", "camber", v)} hint={specHint(specs.rear.camber)} />
              <StepField label="RR Camber" testId="input-rr-camber" value={m.rearRight.camber} onChange={(v) => setWheel("rearRight", "camber", v)} hint={specHint(specs.rear.camber)} />
              <div className="col-span-2">
                <StepField label="Rear Total Toe" testId="input-rear-toe" step={toeStep} value={toeInputValue(m.rearToe)} onChange={(v) => setFlat("rearToe", v === "" ? "" : toeToDeg(v))} hint={toeHint(specs.rear.totalToe)} />
              </div>
            </div>
          </section>

          {/* RESULTS TABLE */}
          <section data-testid="section-results" className="rounded-md bg-surface border border-steel p-5 sm:p-6 animate-rise">
            <h2 className="font-heading font-bold uppercase tracking-widest text-lg mb-4">Diagnostic Readout</h2>
            <div className="space-y-2">
              {results.checks.map((c) => {
                const meta2 = STATUS_META[c.status];
                const Icon = c.status === "pass" ? CheckCircle2 : c.status === "warn" ? AlertTriangle : c.status === "fail" ? XCircle : null;
                return (
                  <div key={c.key} data-testid={`result-${c.key}`} className="flex items-center justify-between gap-3 rounded-sm px-3 py-2 bg-ink border border-steel/60">
                    <span className="font-mono text-xs text-slate-300 uppercase tracking-wide">{c.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-white tabular-nums">
                        {c.value === "" || c.value === null || c.value === undefined
                          ? "—"
                          : (c.key === "front-toe" || c.key === "rear-toe")
                            ? fmtToe(c.value)
                            : `${Number(c.value) >= 0 ? "+" : ""}${Number(c.value).toFixed(2)}°`}
                      </span>
                      <span className="flex items-center gap-1 font-mono text-[10px] font-bold tracking-widest px-2 py-1 rounded-sm"
                        style={{ color: meta2.color, background: meta2.bg }}>
                        {Icon && <Icon size={12} />} {meta2.label}
                      </span>
                    </div>
                  </div>
                );
              })}
              {results.cross.map((c) => (
                <div key={c.key} data-testid={`result-${c.key}`} className="flex items-center justify-between gap-3 rounded-sm px-3 py-2 bg-ink border border-steel/60">
                  <span className="font-mono text-xs text-slate-300 uppercase tracking-wide">{c.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-white tabular-nums">{c.diff.toFixed(2)}° / max {c.max.toFixed(2)}°</span>
                    <span className="font-mono text-[10px] font-bold tracking-widest px-2 py-1 rounded-sm"
                      style={{ color: STATUS_META[c.status].color, background: STATUS_META[c.status].bg }}>
                      {STATUS_META[c.status].label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* SIDEBAR: visualizer + score */}
        <div className="lg:col-span-4 space-y-6">
          <div className="lg:sticky lg:top-24 space-y-6">
            <WheelVisualizer measurements={m} results={results} />

            <div data-testid="score-card" className="rounded-md bg-surface border border-steel p-6 text-center animate-rise">
              <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Overall Alignment Score</div>
              <div className="font-heading font-black text-6xl tabular-nums" style={{ color: scoreColor }}>
                {results.filled === 0 ? "--" : results.passRate}
                <span className="text-2xl">{results.filled === 0 ? "" : "%"}</span>
              </div>
              <div className="font-mono text-xs text-slate-400 mt-1">
                {results.filled === 0 ? "AWAITING INPUT" : `${results.passing}/${results.filled} READINGS IN SPEC`}
              </div>
              <div className="mt-4 flex items-center justify-center gap-2 font-mono text-xs tracking-widest uppercase font-bold"
                style={{ color: scoreColor }}>
                <ShieldCheck size={15} />
                {results.filled === 0 ? "STANDBY" : results.hasFail ? "ADJUSTMENT NEEDED" : results.passing < results.filled ? "MARGINAL" : "ALL GREEN"}
              </div>

              <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="btn-save-session" disabled={results.filled === 0}
                    className="w-full mt-6 bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                    <Save size={15} /> Save Session
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface border-steel text-white">
                  <DialogHeader>
                    <DialogTitle className="font-heading uppercase tracking-widest">Save Alignment Session</DialogTitle>
                    <DialogDescription className="text-slate-400">Store these readings to your alignment log.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">Title</Label>
                      <Input data-testid="save-title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                        placeholder="e.g. Post lift-kit alignment" className="mt-1 bg-ink border-steel focus-visible:ring-amber" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">Mileage</Label>
                        <Input data-testid="save-mileage" type="number" value={meta.mileage} onChange={(e) => setMeta({ ...meta, mileage: e.target.value })}
                          placeholder="92000" className="mt-1 bg-ink border-steel focus-visible:ring-amber" />
                      </div>
                      <div>
                        <Label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">Tread Depth</Label>
                        <Input data-testid="save-tread" value={meta.treadDepth} onChange={(e) => setMeta({ ...meta, treadDepth: e.target.value })}
                          placeholder="8/32 in" className="mt-1 bg-ink border-steel focus-visible:ring-amber" />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-[10px] tracking-widest uppercase text-slate-400">Notes</Label>
                      <Textarea data-testid="save-notes" value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })}
                        placeholder="Shop, cam bolt turns, observations…" className="mt-1 bg-ink border-steel focus-visible:ring-amber" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button data-testid="btn-confirm-save" onClick={handleSave} disabled={saveMutation.isPending}
                      className="bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold">
                      {saveMutation.isPending ? "Saving…" : "Save to Log"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
